import { customerService } from "@api/customer";
import type { Customer } from "@api/types";
import { getCustomerSource, parseNumber } from "@lib/utils";
import { Modal } from "@ui/components/modal";
import { SubmitButton, TextField } from "@ui/form";
import useActiveGym from "@ui/hooks/active-gym";
import useMatchMutate from "@ui/hooks/match-mutate";
import type { FormValues } from "@ui/types";
import { pick } from "lodash";
import { useRouter } from "next/router";
import { Form } from "react-final-form";
import { toast } from "sonner";
import { ApiError, handleApiError } from "@/api/utils";
import * as analytics from "@/lib/analytics";

export interface FormModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  customer?: Customer;
  onSuccess?: any;
}

const initialValues = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
};

function validate(formValues: Record<string, any>): Record<string, string> {
  const errors: Record<string, any> = {};

  if (!formValues.firstName) {
    errors.firstName = "Required";
  }
  if (!formValues.lastName) {
    errors.lastName = "Required";
  }
  if (!formValues.phoneNumber && !formValues.email) {
    errors.phoneNumber = "A phone number or email is required";
    errors.email = "A phone number or email is required";
  }
  return errors;
}

export function FormModal(props: FormModalProps) {
  const router = useRouter();
  const { gym } = useActiveGym();
  const matchMutate = useMatchMutate();
  const customerSource = props.customer ? getCustomerSource(props.customer) : null;

  async function onSubmit(formValues: FormValues) {
    if (!gym) return;
    try {
      if (props.customer) {
        await customerService.readAndUpdate(
          props.customer.id,
          pick(formValues, ["firstName", "lastName", "phoneNumber", "email"]),
        );
        analytics.track("Contact Updated", { gym: gym.name });
      } else {
        const doc = await customerService.create({ gym: gym.id, ...formValues });
        router.push(`/contacts/${doc.id}`);
        analytics.track("Contact Created", { gym: gym.name });
      }
      toast.success(props.customer ? "Contact updated" : "Contact created");
      await matchMutate(/\/customers/);
      props.onSuccess?.();
      props.setOpen(false);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
      throw err;
    }
  }

  return (
    <Modal
      title={props.customer ? "Edit contact" : "Create new contact"}
      size="xs"
      open={props.open}
      setOpen={props.setOpen}
    >
      <Form
        onSubmit={onSubmit}
        validate={validate}
        initialValues={props.customer || initialValues}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <div className="space-y-4">
                  <div className="sm:grid-cols-2 grid grid-cols-1 gap-4">
                    <TextField name="firstName" label="First name" />
                    <TextField name="lastName" label="Last name" />
                  </div>
                  <TextField
                    type="tel"
                    name="phoneNumber"
                    label="Phone number"
                    parse={parseNumber()}
                  />
                  <TextField name="email" label="Email address" />
                  {customerSource &&
                  customerSource !== "helios" &&
                  customerSource !== "manual" &&
                  customerSource !== "sms" ? (
                    <p className="text-muted-foreground text-xs">
                      This contact was added from{" "}
                      <span className="capitalize">{customerSource}</span>. Updates in Helios will
                      not reflect in <span className="capitalize">{customerSource}</span> and may be
                      overwritten if the integration is active.
                    </p>
                  ) : null}
                </div>
              </Modal.Content>
              <Modal.Footer>
                <div className="flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    className="btn btn--sm btn--default"
                    onClick={() => props.setOpen(false)}
                  >
                    Cancel
                  </button>
                  <SubmitButton size="sm">Save</SubmitButton>
                </div>
              </Modal.Footer>
            </form>
          );
        }}
      />
    </Modal>
  );
}
